import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  ThumbsDownIcon,
  ThumbsUpIcon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  ThumbsDownIcon as ThumbsDownSolidIcon,
  ThumbsUpIcon as ThumbsUpSolidIcon
} from "@hugeicons-pro/core-solid-rounded";

import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import { submitRecommendationSummaryFeedback } from "api/recommendation-summary/recommendation-summary";
import {
  RecommendationSummary,
  RecommendationSummaryFeedback
} from "api/recommendation-summary/types";
import { Application } from "types/application";
import { getErrorMessage } from "utils/error";

interface AiSummaryFeedbackProps {
  applicationId: number;
  summary: RecommendationSummary;
  onTurnOff: () => void;
}

export const AiSummaryFeedback = ({
  applicationId,
  summary,
  onTurnOff
}: AiSummaryFeedbackProps) => {
  const queryClient = useQueryClient();
  const [localFeedback, setLocalFeedback] =
    useState<RecommendationSummaryFeedback | null>(summary.feedback);

  useEffect(() => {
    setLocalFeedback(summary.feedback);
  }, [summary.feedback]);

  const feedbackMutation = useMutation({
    mutationKey: ["RECOMMENDATION_SUMMARY_FEEDBACK", applicationId, "UPDATE"],
    mutationFn: (feedback: RecommendationSummaryFeedback) =>
      submitRecommendationSummaryFeedback(applicationId, summary.id, feedback),
    onSuccess: (updated) => {
      // The summary is merged into the application detail outside its cache,
      // so patch the query instead of refetching the whole application.
      queryClient.setQueryData<Application>(
        ["APPLICATION", String(applicationId)],
        (current) =>
          current ? { ...current, recommendation_summary: updated } : current
      );
      toast.success("Thank you for your feedback");
    },
    onError: (error) => {
      setLocalFeedback(summary.feedback);
      toast.error(getErrorMessage(error));
    }
  });

  // The API only stores a vote, it cannot clear one: re-clicking the active
  // vote is a no-op, switching to the other one overwrites it.
  const vote = (feedback: RecommendationSummaryFeedback) => {
    if (localFeedback === feedback) return;
    setLocalFeedback(feedback);
    feedbackMutation.mutate(feedback);
  };

  const isUpvote = localFeedback === "upvote";
  const isDownvote = localFeedback === "downvote";

  return (
    <div className="flex items-center gap-2">
      <IconButton
        variant={IconButtonVariant.TEXT}
        size={IconButtonSize.SM}
        HugeIcon={isUpvote ? ThumbsUpSolidIcon : ThumbsUpIcon}
        strokeWidth={isUpvote ? 1 : undefined}
        onClick={() => vote("upvote")}
        disabled={feedbackMutation.isPending}
        tooltip="Helpful"
        className="!text-content-200 hover:!text-content-700 hover:-rotate-12 hover:scale-110"
      />
      <IconButton
        variant={IconButtonVariant.TEXT}
        size={IconButtonSize.SM}
        HugeIcon={isDownvote ? ThumbsDownSolidIcon : ThumbsDownIcon}
        strokeWidth={isDownvote ? 1 : undefined}
        onClick={() => vote("downvote")}
        disabled={feedbackMutation.isPending}
        tooltip="Not helpful"
        className="!text-content-200 hover:!text-content-700 hover:rotate-6 hover:scale-110"
      />
      <button
        type="button"
        className="ml-2 text-[13px] font-bold text-content-200 hover:text-content-400 transition-colors"
        onClick={onTurnOff}
      >
        Don’t show it again
      </button>
    </div>
  );
};
